import React, { ReactElement, useCallback, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import styles from './SignInMethods.module.scss'
import { AlertTypes, ModalTypes, SideRail, Spinner, SpinnerSizeTypes, StandardModal, Tag, TagType, Alert } from '@digicert/dcone-common-ui'
import { Translate } from 'react-localize-redux'
import { translate } from '../../../../shared/helpers/utils'
import { enableMfa, fetchTenantById, editStandardSignIn } from '../../Tenants.actions'
import { IAccessScope } from '../../../../reducers/Reducers.interfaces'
import { EditingTypes, ExtendedSignInMethodsProps } from './SignInMethods.types'
import cn from 'classnames'
import SsoConfig from '../../../../shared/components/sso-config/SsoConfig'
import { DISABLED, ENABLED, FeatureFlags } from '../../../../core/constants'
import OidcConfig from 'shared/components/oidc-config/OidcConfig'
import { disableSigninMethod, getAccountSignIn } from 'secure-pages/tenants/create-account-sign-in/AccountSignIn.actions'
import { isValid } from 'secure-pages/tenants/create-account-sign-in/CertificateParser'
import TwoFactorAuthentication from 'shared/components/two-factor-auth/TwoFactorAuthentication'
import { updateClientAuthCertLogin, fetchFeatureFlag, updateClientAuthCert1FA } from './SignInMethods.actions'
import SCIMProvisioning from 'shared/components/scim-provisioning/SCIMProvisioning'
import { getSCIMConfig } from 'shared/components/scim-provisioning/SCIMProvisioning.actions'
import { IVerificationType } from 'shared/components/two-factor-auth/TwoFactorAuth.types'
import { ISCIMConfig } from 'shared/components/scim-provisioning/SCIMProvisioning.types'

const SignInMethods = (
  {
    account,
    canEdit,
    accessScope,
    fetchTenantById,
    setAccount,
    editStandardSignIn,
    getAccountSignIn,
    updateClientAuthCertLogin,
    updateClientAuthCert1FA,
    getSCIMConfig,
    fetchFeatureFlag,
    canEditSCIM
  }: ExtendedSignInMethodsProps): ReactElement => {
  const ssoMethodSaml = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnSaml')
  const standardSignIn = account?.sign_in_methods.find(item => item.signInMethod === 'standard')
  const ssoMethodOidc = account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnOidc')
  const [clientCertAuthModal, setClientCertAuthModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [showSideRail, setShowSideRail] = useState(false)
  const [sideRailContent, setSideRailContent] = useState('')
  const [ssoConfigKey, setSsoConfigKey] = useState(0)
  const [ssoSamlEnabled, setSsoSamlEnabled] = useState(ssoMethodSaml?.status === ENABLED)
  const [cacEnabled, setCacEnabled] = useState(account?.sign_in_methods.find(item => item.signInMethod === 'standard')?.clientAuthCertLoginEnabled === true)
  const [cacEnabled1FA, setCacEnabled1FA] = useState(account?.sign_in_methods.find(item => item.signInMethod === 'standard')?.clientAuthCert1faEnabled === true)
  const [oidcEnabled, setOidcEnabled] = useState(ssoMethodOidc?.status === ENABLED)
  const [disableStandardSignIn, setDisableStandardSignIn] = useState(disableSigninMethod(account, 'standard'))
  const [isSamlCertExpired, setIsSamlCertExpired] = useState(false)
  const [infoModalData, setInfoModalData] = useState<any>({ headline: '', children: '' })
  const [scimConfig, setScimConfig] = useState<ISCIMConfig | null>(null)
  const [enableSCIM, setEnableSCIM] = useState(false)
  const [showSCIM, setShowSCIM] = useState(false)

  useEffect(() => {
    fetchFeatureFlag(FeatureFlags.ENABLE_SCIM_CONFIGURATION, account.id).then((response) => {
      setShowSCIM(response)
      if (response) {
        getSCIMConfig(account.id).then((response: ISCIMConfig) => {
          setScimConfig(response)
          setEnableSCIM(response?.status === 'ENABLED')
        })
      }
    })
  }, [account.id])

  useEffect(() => {
    setDisableStandardSignIn(disableSigninMethod(account, 'standard'))
    setSsoSamlEnabled(account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnSaml')?.status === ENABLED)
    setOidcEnabled(account?.sign_in_methods.find(item => item.signInMethod === 'singleSignOnOidc')?.status === ENABLED)
    setCacEnabled(account?.sign_in_methods.find(item => item.signInMethod === 'standard')?.clientAuthCertLoginEnabled === true)
    setCacEnabled1FA(account?.sign_in_methods.find(item => item.signInMethod === 'standard')?.clientAuthCert1faEnabled === true)
    const federationProvider = account.auth_config ? account.auth_config.find(item => item.type === 'SAML 2.0') : ''
    const federationId = federationProvider ? federationProvider.id : ''
    if (federationId) {
      getAccountSignIn(federationId).then(response => {
        const expirationDate = response?.identity_provider_config?.certificates[0]?.expiration_date
        const status = response?.status
        if (expirationDate && status.toLowerCase() === ENABLED) {
          setIsSamlCertExpired(!isValid(response?.identity_provider_config?.certificates[0]?.expiration_date))
        } else {
          setIsSamlCertExpired(false)
        }
      })
    }
  }, [account])

  useEffect(() => {
    setEnableSCIM(scimConfig?.status === 'ENABLED')
  }, [scimConfig])

  const updateSignInMethods = useCallback(
    () => {
      fetchTenantById(account.id).then((response) => {
        setAccount({ ...response })
      })
      setShowSideRail(false)
    }, []
  )

  const updateCAC = () => {
    const payload = {
      enabled: !cacEnabled1FA,
      cac_config: {}
    }
    const notificationMessage = () => {
      if (cacEnabled1FA) {
        return <Translate id='notifications.cac.disabled' data={{ name: <b>{account.name}</b> }} />
      }
      else {
        return <Translate id='notifications.cac.enabled' data={{ name: <b>{account.name}</b> }} />
      }
    }
    updateClientAuthCert1FA(payload, account.id, notificationMessage()).then(() => {
      setClientCertAuthModal(false)
      updateSignInMethods()
    })
  }

  const submitPasswordSignIn = async (): Promise<void> => {
    if (standardSignIn?.status === ENABLED && disableStandardSignIn) { // if SSO is not enabled, do not allow to disable password sign-in
      setShowPasswordModal(false)
      setInfoModalData({
        headline: <Translate id='editStandardSignIn.cannotDisableOnlySignInMethod' />,
        children: <><p><Translate id='editStandardSignIn.cannotDisableOnlySignInMethodDesc' /></p>
          <p><Alert
            description={<Translate id='editStandardSignIn.disablePasswordSignNote' />}
            bannerType={AlertTypes.INFO}
            showDismissIcon
          /></p></>
      })
      setShowInfoModal(true)
      return
    }
    const message = standardSignIn?.status !== ENABLED ? <Translate id='notifications.standardSignIn.enabled' data={{ name: <b>{account.name}</b> }} /> : <Translate id='notifications.standardSignIn.disabled' data={{ name: <b>{account.name}</b> }} />
    const payload = {
      name: account ? account.name : '',
      'service_period': {
        from: account ? account.service_period.from : '',
        to: account ? account.service_period.to : ''
      },
      sign_in_methods: account ? account.sign_in_methods : null,
      standard_login: standardSignIn?.status !== ENABLED
    }
    await editStandardSignIn(account.id, payload, message, true)
    setShowPasswordModal(false)
    updateSignInMethods()
  }

  const editSSO = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.SSO_SAML)
    setSsoConfigKey(prev => prev + 1)
  }

  const editOidc = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.OIDC)
  }

  const editTwoFactorAuth = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.TFA)
  }

  const editSCIMProvisioning = () => {
    setShowSideRail(true)
    setSideRailContent(EditingTypes.SCIM_PROVISIONING)
  }

  const loadSideRailContent = useCallback(
    (sideRailContent: string) => {
      switch (sideRailContent) {
        case EditingTypes.SSO_SAML:
          // todo rsatyend missing types match, history, location
          // @ts-ignore
          return <SsoConfig account={account} onCancel={() => setShowSideRail(false)} ssoSaml={ssoSamlEnabled} onChange={updateSignInMethods} key={ssoConfigKey}/>
        case EditingTypes.OIDC:
          // todo rsatyend missing types match, history, location
          // @ts-ignore
          return <OidcConfig onCancel={() => setShowSideRail(false)} ssoOidc={oidcEnabled} onChange={updateSignInMethods} account={account} setOidcEnabled={setOidcEnabled} />
        case EditingTypes.TFA:
          return <TwoFactorAuthentication accessScope={accessScope} onCancel={() => setShowSideRail(false)} account={account} updateDetails={updateSignInMethods} />
        case EditingTypes.SCIM_PROVISIONING:
          return <SCIMProvisioning onCancel={() => setShowSideRail(false)} accountId={account.id} scimConfig={scimConfig} setScimConfig={setScimConfig} />
        default:
          return <Spinner size={SpinnerSizeTypes.SMALL} />
      }
    }, [sideRailContent, ssoSamlEnabled, account, scimConfig, ssoConfigKey]
  )

  const loadSideRailTitle = useCallback(
    (sideRailContent: string) => {
      switch (sideRailContent) {
        case EditingTypes.SSO_SAML:
          return translate('ssoSaml.singleSignOnWithSaml') as string
        case EditingTypes.OIDC:
          return translate('common.form.singleSignOnOidc') as string
        case EditingTypes.TFA:
          return translate('editStandardSignIn.TFAHeadline') as string
        case EditingTypes.SCIM_PROVISIONING:
          return translate('accounts.details.scim.headline') as string
      }
    }, [sideRailContent]
  )

  const editStandardSign = () => {
    setShowPasswordModal(true)
  }

  const editCAC = () => {
    if (account?.mfa_types.includes(IVerificationType.CAC)) {
      setInfoModalData({
        headline: <h1 className={styles.cacInfoHeading}><Translate id='accounts.details.signInMethods.cacInfoPopupHeadline' /></h1>,
        children: <p><Translate id='accounts.details.signInMethods.cacInfoPopupDescription' /></p>
      })
      setShowInfoModal(true)
    } else if (cacEnabled1FA && disableSigninMethod(account, 'cac')) {
      setInfoModalData({
        headline: <Translate id='editStandardSignIn.cannotDisableOnlySignInMethod' />,
        children: <><p><Translate id='editStandardSignIn.cannotDisableOnlySignInMethodCacDesc' /></p>
          <p><Alert
            description={<Translate id='editStandardSignIn.disableCacSignNote' />}
            bannerType={AlertTypes.INFO}
            showDismissIcon
          /></p></>
      })
      setShowInfoModal(true)
    } else {
      setClientCertAuthModal(true)
    }
  }

  return (
    <section className={styles.signInMethods}>
      <header className={styles.signInHeader}>
        <h1><Translate id='accounts.details.signInMethods.headline' /></h1>
        <p><Translate id='accounts.details.signInMethods.description' /></p>
      </header>
      {isSamlCertExpired && (
        <div className={styles.banner}>
          <Alert
            bannerType={AlertTypes.ERROR}
            description={translate('ssoSaml.expiredSamlCertificateDesc') as string}
            showDismissIcon
            title={translate('ssoSaml.expiredSamlCertificateTitle') as string}
            rounded={false}
          />
        </div>
      )}

      <p className={styles.subTitle}>{translate('accounts.details.signInMethods.primarySignInMethods') as string}</p>
      <div className={styles.signInOptions}>
        <button className={cn(canEdit ? styles.signInOption : styles.inActive)} onClick={canEdit ? editStandardSign : undefined}>
          <div>
            <p className={styles.label}><Translate id='accounts.details.signInMethods.passwordAuthentication' /></p>
            <p className={styles.description}><Translate
              id='accounts.details.signInMethods.passwordAuthenticationDescription' /></p>
          </div>
          <Tag type={standardSignIn?.status === ENABLED ? TagType.ACTIVE : TagType.INACTIVE}>
            {standardSignIn?.status === ENABLED
              ? <Translate id={'common.statuses.enabled'} />
              : <Translate id={'common.statuses.disabled'} />
            }
          </Tag>
        </button>
        <button className={cn(canEdit ? styles.signInOption : styles.inActive)} onClick={canEdit ? editCAC : undefined}>
          <div>
            <p className={styles.label}>
              <Translate id='common.form.clientAuthCert' />
            </p>
            <p className={styles.description}>
              <Translate id='common.form.cacDescription' />
            </p>
          </div>
          <Tag type={cacEnabled1FA ? TagType.ACTIVE : TagType.INACTIVE}>
            <Translate id={cacEnabled1FA ? 'common.statuses.enabled' : 'common.statuses.disabled'} />
          </Tag>
        </button>
        <button className={cn(canEdit ? styles.signInOption : styles.inActive)} onClick={canEdit ? editSSO : undefined}>
          <div>
            <p className={styles.label}>
              <Translate id='ssoSaml.singleSignOnWithSaml' />
            </p>
            <p className={styles.description}>
              <Translate id='ssoSaml.singleSignOnWithSamlDescription' />
            </p>
          </div>
          <Tag type={ssoSamlEnabled ? TagType.ACTIVE : TagType.INACTIVE}>
            <Translate id={ssoSamlEnabled ? 'common.statuses.enabled' : 'common.statuses.disabled'} />
          </Tag>
        </button>
        <button className={cn(canEdit ? styles.signInOption : styles.inActive)} onClick={canEdit ? editOidc : undefined}>
          <div>
            <p className={styles.label}>
              <Translate id='common.form.singleSignOnOidc' />
            </p>
            <p className={styles.description}>
              <Translate id='ssoSaml.oidcDescription' />
            </p>
          </div>
          <Tag type={oidcEnabled ? TagType.ACTIVE : TagType.INACTIVE}>
            <Translate id={oidcEnabled ? 'common.statuses.enabled' : 'common.statuses.disabled'} />
          </Tag>
        </button>

      </div>
      <p className={styles.subTitle}>{translate('accounts.details.signInMethods.additionalAuthentication') as string}</p>
      <div className={styles.signInOptions}>
        <button
          className={cn(canEdit && account.can_edit_mfa && (accessScope === IAccessScope.system || accessScope === IAccessScope.account) ? styles.signInOption : styles.inActive)}
          onClick={canEdit && account.can_edit_mfa && (accessScope === IAccessScope.system || accessScope === IAccessScope.account)? editTwoFactorAuth : undefined}
        >
          <div>
            <p className={styles.label}>{translate('common.form.signInMethodTFA') as string}</p>
            <p className={styles.description}><Translate id='accounts.details.signInMethods.2faMessage' />
            </p>
          </div>
          <Tag type={account?.mfa_enabled ? TagType.ACTIVE : TagType.INACTIVE}>
            {account?.mfa_enabled
              ? <Translate id={'common.statuses.enabled'} />
              : <Translate id={'common.statuses.disabled'} />
            }
          </Tag>
        </button>
      </div>
      {showSCIM && <><p className={styles.subTitle}>{translate('accounts.details.signInMethods.userLifecycleAndProvisioning') as string}</p>
        <div className={styles.signInOptions}>
          <button
            className={cn(canEditSCIM ? styles.signInOption : styles.inActive)}
            onClick={canEditSCIM ? editSCIMProvisioning : undefined}
          >
            <div>
              <p className={styles.label}>{translate('accounts.details.scim.title') as string}</p>
              <p className={styles.description}><Translate id='accounts.details.scim.description' />
              </p>
            </div>
            <Tag type={enableSCIM ? TagType.ACTIVE : TagType.INACTIVE}>
              {enableSCIM
                ? <Translate id={'common.statuses.enabled'} />
                : <Translate id={'common.statuses.disabled'} />
              }
            </Tag>
          </button>
        </div></>}

      {clientCertAuthModal && (
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={cacEnabled1FA ? <Translate id='common.form.disableCAC' /> :
            <Translate id='common.form.enableCAC' />}
          display={clientCertAuthModal}
          buttonLabel={cacEnabled1FA ? <Translate id='common.form.buttons.disable' /> : <Translate id='common.form.buttons.enable' />}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          onOkCallBack={updateCAC}
          onCancelCallBack={() => setClientCertAuthModal(false)}
        >
          {cacEnabled
            ? (<Translate id='common.form.disableCACDescription' />)
            : (
              <Translate id='common.form.enableCACDescription' />)
          }
        </StandardModal>
      )}

      {showPasswordModal && (
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={standardSignIn?.status === DISABLED ? <Translate id='editStandardSignIn.enablePasswordSignIn' /> : <Translate id='editStandardSignIn.disablePasswordSignIn' />}
          display={showPasswordModal}
          buttonLabel={standardSignIn?.status === DISABLED ? <Translate id='common.form.buttons.enablePassword' /> : <Translate id='common.form.buttons.disablePassword' />}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          onOkCallBack={submitPasswordSignIn}
          onCancelCallBack={() => setShowPasswordModal(false)}
        >
          {standardSignIn?.status === DISABLED
            ? <Translate id='editStandardSignIn.enablePasswordSignInDesc' />
            : <p><Translate id='editStandardSignIn.disablePasswordSignInDesc' /></p>
          }
        </StandardModal>
      )}

      {showInfoModal && (
        <StandardModal
          modalType={ModalTypes.STANDARD}
          headline={infoModalData.headline}
          display={showInfoModal}
          buttonLabel={<Translate id='common.form.buttons.closeBtn' />}
          onOkCallBack={() => setShowInfoModal(false)}
          hideCancelButton={true}
          maskClosable={true}
          onCancelCallBack={() => setShowInfoModal(false)}
          buttonCancelLabel={''}
        >
          {infoModalData.children}
        </StandardModal>
      )}

      <SideRail
        className={cn(styles.sideRail, showSideRail && styles.expanded)}
        headline={<h2>{loadSideRailTitle(sideRailContent)}</h2>}
        show={showSideRail}
        onDismiss={() => setShowSideRail(!showSideRail)}
        lightbox
      >
        <div className={styles.sideRailContent}>
          {loadSideRailContent(sideRailContent)}
        </div>
      </SideRail>

    </section>
  )
}

export default connect(
  null,
  { enableMfa, fetchTenantById, editStandardSignIn, updateClientAuthCertLogin, updateClientAuthCert1FA, getAccountSignIn, getSCIMConfig, fetchFeatureFlag }
)(SignInMethods)
